import React, { useCallback, useEffect, useRef, useState } from "react";
import i18n from "i18next";
import InteractiveItemEditor from "../../_editor/components/interactive_item/InteractiveItemEditor";
import InteractiveAiEdit from "../../_editor/components/interactive_item/InteractiveAiEdit";
import { useHtmlByUrl } from "../../common/html_artifact_store.es6";

/* eslint-disable react/prop-types */

/**
 * InteractiveItem kutusunun gövdesi — ve puan köprüsünün studio tarafı.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * KÖPRÜ
 * ──────────────────────────────────────────────────────────────────────────
 * Widget ayrı bir origin'deki iframe'de çalışıyor, yani kursun state'ine
 * doğrudan dokunamaz. İki yönlü tek bir postMessage kanalı var:
 *
 *   ↓ host → widget   { type:'item', item }    veri (ilk yükleme ve her düzenleme)
 *   ↑ widget → host   { type:'ready' }         "bana veriyi yolla"
 *                     { type:'report', correct, total }   koşan skor
 *                     { type:'complete' }      "öğrenci bitirdi"
 *
 * Mesajlar SADECE bu iframe'in contentWindow'undan kabul edilir. Bir sahnede
 * iki aktivite varsa, origin'e bakan bir filtre ikisini ayıramaz — ikisi de
 * aynı blob depolamadan gelir — ve birinin skoru diğerinin kutusuna yazılır.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * PUAN NEDEN HER RAPORDA DEĞİL, BİR KEZ YAZILIR
 * ──────────────────────────────────────────────────────────────────────────
 * `setAnswer` ucuz: cevabı kutunun egzersiz kaydına koyar, hiçbir şeyi
 * kilitlemez. `submitPage` ise puanı VERİR ve her çağrıldığında `tryCount`
 * artırır — ikinci denemede puan %60'a, üçüncüde %30'a düşer (GradeComponent).
 *
 * Widget her eşleşmede skorunu bildiriyor. Her bildirimde submit etseydik,
 * altı çiftlik bir hafıza oyununu kusursuz oynayan öğrenci sıfır alırdı.
 * Bu yüzden: her `report` yalnızca setAnswer, tek `complete` ise submit.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * EDİTÖRDE FARE
 * ──────────────────────────────────────────────────────────────────────────
 * Scene3D'deki ikilemin aynısı: aktivite ancak fare olaylarını alırsa
 * oynanabilir, kutu ise ancak ALMAZSA taşınıp boyutlandırılabilir. Seçim
 * yazarın — "Dene" düğmesi. Visor'da ikilem yok, aktivite her zaman
 * etkileşimli.
 */
export default function InteractiveItemBox({ state, boxId, selected, visor, pluginProps }) {
  // Doküman iframe'e SRC OLARAK VERİLMEZ, çekilip srcdoc olarak verilir.
  //
  // Kaydedilmiş bir kursta `htmlUrl` artık ham blob adresi değil, varlık
  // geçidinin adresidir (`/api/f/<id>`). Geçit text/html'i bilerek inline
  // SERVİS ETMEZ — yüklenmiş bir .html'in bizim origin'imizde aktif içerik
  // olarak çalışmasını engelleyen depolanmış-XSS koruması — ve onu
  // `application/octet-stream` + `attachment` olarak verir. Tarayıcı bunu bir
  // İNDİRME sayar; `sandbox="allow-scripts"` çerçevesinin indirme hakkı
  // yoktur, indirme engellenir ("Download is disallowed … the flag
  // 'allow-downloads' is not set") ve geriye BEMBEYAZ bir çerçeve kalır.
  // Hata konsolda durur, sayfada hiçbir iz bırakmaz.
  //
  // `useHtmlByUrl` motion ve 3B sahnelerin aynı duvara çarpıp çıkardığı yol:
  // geçit adresini Bearer ile ÇEKER ve metni döndürür; başka bir host'un düz
  // http(s) adresi ise `directSrc` olarak geri gelir ve eskisi gibi src ile
  // yüklenir (Bearer oraya asla gitmez).
  const rawUrl = (state && state.htmlUrl) || "";
  const { html, failed, directSrc } = useHtmlByUrl(rawUrl);
  const item = (state && state.item) || null;

  const frameRef = useRef(null);
  const [tryMode, setTryMode] = useState(false);
  const [editing, setEditing] = useState(false);
  const [aiEditing, setAiEditing] = useState(false);
  // Son bildirilen skor. `complete` geldiğinde submit edilecek olan bu.
  const latest = useRef({ correct: 0, total: 0 });
  // Bir kutu bir kez submit eder. Widget `complete`'i iki kez yollarsa
  // (yeniden oynandı, bir kenar durumu) ikincisi öğrencinin puanını düşürür.
  const submitted = useRef(false);

  /** Veriyi widget'a yolla. */
  const pushItem = useCallback(() => {
    const frame = frameRef.current;
    if (!frame || !frame.contentWindow || !item) return;
    try {
      frame.contentWindow.postMessage(
        { source: "aidea-interactive-host", type: "item", item },
        "*"
      );
    } catch (e) {
      /* iframe henüz yüklenmemiş olabilir; 'ready' geldiğinde tekrar denenir */
    }
  }, [item]);

  // Veri değiştiğinde (Düzenle ekranından) widget anında yeniden çizilsin.
  useEffect(() => {
    pushItem();
  }, [pushItem]);

  useEffect(() => {
    const onMessage = (ev) => {
      const data = ev && ev.data;
      if (!data || data.source !== "aidea-interactive") return;
      // Yalnızca BU iframe — bkz. yukarıdaki not.
      const frame = frameRef.current;
      if (!frame || ev.source !== frame.contentWindow) return;

      if (data.type === "ready") {
        pushItem();
        return;
      }

      // Editörde skor yazılmaz: yazar aktiviteyi deniyor, sınava girmiyor.
      if (!visor || !pluginProps) return;

      if (data.type === "report") {
        latest.current = {
          correct: Number(data.correct) || 0,
          total: Number(data.total) || 0,
        };
        // Kilitlemez, yalnızca cevabı kaydeder.
        pluginProps.setAnswer?.(latest.current);
        return;
      }

      if (data.type === "complete") {
        if (submitted.current) return;
        submitted.current = true;
        // Puansız aktivitede total 0 kalır; checkAnswer false döner ve kutu
        // puana hiç girmez — ama "görüldü" bilgisi yine de işlenir.
        pluginProps.setAnswer?.(latest.current);
        pluginProps.submitPage?.(pluginProps.currentView, boxId);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [pushItem, visor, pluginProps, boxId]);

  // Seçili kutunun üstündeki pill menüsü (EditorShortcuts) bu iki ekranı
  // açar. Bir CustomEvent üzerinden konuşuluyor çünkü pill, canvas'ın
  // tepesinde ayrı bir portal'da yaşıyor — props ile bağlamak, bu özelliğin
  // state'ini EditorShortcuts'a taşımak demek olurdu.
  useEffect(() => {
    const onOpen = (ev) => {
      if (ev?.detail?.boxId !== boxId) return;
      if (ev.type === "aidea:interactive-ai-edit") setAiEditing(true);
      else setEditing(true);
    };
    window.addEventListener("aidea:interactive-ai-edit", onOpen);
    window.addEventListener("aidea:interactive-edit", onOpen);
    return () => {
      window.removeEventListener("aidea:interactive-ai-edit", onOpen);
      window.removeEventListener("aidea:interactive-edit", onOpen);
    };
  }, [boxId]);

  // Kutu seçimi bırakıldığında deneme modu kapanır, yoksa sahnede görünmez
  // bir "fareyi yutan" dikdörtgen kalır ve yazar neden tıklayamadığını
  // anlayamaz.
  useEffect(() => {
    if (!selected && tryMode) setTryMode(false);
  }, [selected, tryMode]);

  // Yüklenemeyen bir doküman SÖYLENİR. Boş çerçeve, yazara "bu kutu bozuk mu,
  // yoksa aktivite mi boş" sorusunu sordurur ve ikisi aynı şeye benzer.
  if (!rawUrl || failed) {
    return (
      <div style={EMPTY_STYLE}>
        {rawUrl
          ? i18n.t("InteractiveItem.LoadFailed", {
              defaultValue: "İnteraktif öğe yüklenemedi",
            })
          : i18n.t("InteractiveItem.Empty", {
              defaultValue: "İnteraktif öğe bulunamadı",
            })}
      </div>
    );
  }

  const frame = (
    <iframe
      ref={frameRef}
      srcDoc={directSrc ? undefined : html || ""}
      src={directSrc || undefined}
      sandbox="allow-scripts"
      title={(state && state.label) || "Interactive"}
      scrolling="no"
      onLoad={pushItem}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        background: "transparent",
        pointerEvents: visor || tryMode ? "auto" : "none",
      }}
    />
  );

  if (visor) return frame;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {frame}
      {/* Yalnızca kutu seçiliyken — sahnedeki her aktivitenin üstünde duran
          iki düğme, sahnenin kendisini okunmaz hale getirir. */}
      {selected && (
        <div style={BAR_STYLE}>
          {/* "Düzenle" ve "AI ile düzenle" kutunun üstündeki pill menüsünde
              (EditorShortcuts) — görsel ve video kutularıyla aynı yerde.
              Burada yalnızca "Dene" kalıyor, çünkü o bir görünüm değil bir
              ETKİLEŞİM anahtarı: iframe'in fare olaylarını alıp almayacağını
              belirliyor ve kutunun kendisine ait. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setTryMode((v) => !v);
            }}
            style={buttonStyle(tryMode)}
            title={
              tryMode
                ? i18n.t("InteractiveItem.TryOffHint", {
                    defaultValue: "Kutuyu taşımak için kapat",
                  })
                : i18n.t("InteractiveItem.TryOnHint", {
                    defaultValue: "Aktiviteyi denemek için aç",
                  })
            }
          >
            {tryMode
              ? i18n.t("InteractiveItem.TryOff", { defaultValue: "✓ Deneme açık" })
              : i18n.t("InteractiveItem.TryOn", { defaultValue: "Dene" })}
          </button>
        </div>
      )}
      {editing && (
        <InteractiveItemEditor boxId={boxId} state={state} onClose={() => setEditing(false)} />
      )}
      {aiEditing && (
        <InteractiveAiEdit boxId={boxId} state={state} onClose={() => setAiEditing(false)} />
      )}
    </div>
  );
}

const EMPTY_STYLE = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f6f7fb",
  color: "#94a3b8",
  fontSize: 13,
};

const BAR_STYLE = {
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 2,
  display: "flex",
  gap: 6,
  pointerEvents: "auto",
};

function buttonStyle(active) {
  return {
    padding: "5px 11px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: `1px solid ${active ? "#7c3aed" : "#dce3f3"}`,
    background: active ? "#7c3aed" : "rgba(255,255,255,.92)",
    color: active ? "#fff" : "#475569",
    boxShadow: "0 1px 4px rgba(15,23,42,.18)",
  };
}
